import { useState, useRef } from "react";
import { useCompaniesFilter } from "@/contexts/CompaniesFilterContext";
import { CompaniesGrid } from "@/components/companies/CompaniesGrid";
import { CompaniesList } from "@/components/companies/CompaniesList";
import { CandidatesViewOptions, SortField } from "@/components/candidates/CandidatesViewOptions";
import { CandidatesPagination } from "@/components/candidates/CandidatesPagination";
import { useCompaniesPaginated } from "@/hooks/companies/useCompaniesPaginated";
import { useUserFavorites } from "@/hooks/favorites/useUserFavorites";
import { Company } from "@/components/CompanyCard";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/AuthProvider";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

// Assigned to me filter component for the sort section
const AssignedToMeFilter = () => {
  const { t } = useTranslation();
  const { assignedToMe, setAssignedToMe } = useCompaniesFilter();

  return (
    <label className="flex items-center gap-2 cursor-pointer px-3 py-2 border border-input rounded-sm hover:bg-accent hover:text-accent-foreground">
      <Checkbox
        id="assigned-to-me"
        checked={assignedToMe}
        onCheckedChange={(checked) => setAssignedToMe(checked as boolean)}
        className="h-4 w-4"
      />
      <span className="text-sm">
        {t("companies.assignedToMe", "Assigned to me")}
      </span>
    </label>
  );
};

interface CompaniesContentProps {
  companies?: Company[];
  isLoading?: boolean;
  onCompaniesChange?: () => void;
}

export const CompaniesContent = ({
  onCompaniesChange
}: CompaniesContentProps) => {
  const { user } = useAuth();
  const {
    searchTerm,
    assignedToMe,
    showFavoritesOnly,
    relationshipFilter,
    sortField,
    setSortField,
    sortOrder,
    setSortOrder,
    viewMode,
    setViewMode
  } = useCompaniesFilter();

  const { favorites } = useUserFavorites('company');

  // Use new paginated hook with database-level filtering
  const {
    companies: paginatedCompanies,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    setCurrentPage,
    refetch
  } = useCompaniesPaginated(
    {
      searchTerm,
      assignedToMe,
      currentUserId: user?.id || null,
      showFavoritesOnly,
      favorites,
      relationshipFilter
    },
    sortField,
    sortOrder
  );

  const { t } = useTranslation();

  const containerRef = useRef<HTMLDivElement>(null);

  // Reset to first page when sort field or order changes
  const handleSortFieldChange = (field: SortField) => {
    setSortField(field);
    setCurrentPage(1);
  };

  const handleDeleteCompanies = async (companyIds: string[]) => {
    try {
      const { error } = await supabase
        .from('companies')
        .delete()
        .in('id', companyIds);

      if (error) throw error;

      refetch();
      toast({
        title: t("common.success"),
        description: t("common.deleteSuccess", { count: companyIds.length }),
      });

      onCompaniesChange?.();
    } catch (error) {
      console.error('Error deleting companies:', error);
      toast({
        title: t("common.error"),
        description: t("common.deleteError"),
        variant: "destructive",
      });
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Direct scroll without any transition
    if (containerRef.current) {
      const scrollableParent = containerRef.current.closest('.overflow-y-auto') as HTMLElement;
      if (scrollableParent) {
        // Force immediate scroll without animation
        scrollableParent.style.scrollBehavior = 'auto';
        scrollableParent.scrollTop = 0;
        scrollableParent.style.scrollBehavior = '';
      }
    }
  };

  return (
    <div ref={containerRef} className="flex flex-col h-full">
      <CandidatesViewOptions 
        sortField={sortField}
        setSortField={handleSortFieldChange}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        viewMode={viewMode}
        setViewMode={setViewMode}
        extra={
          <AssignedToMeFilter />
        }
      />
      
      <div className="flex-1 overflow-y-auto min-h-0">
        {viewMode === "card" ? (
          <CompaniesGrid 
            companies={paginatedCompanies}
            filteredCompanies={paginatedCompanies}
            isLoading={isLoading}
            onDeleteCompanies={handleDeleteCompanies}
            onCompaniesChange={onCompaniesChange}
            favorites={favorites}
          />
        ) : (
          <CompaniesList 
            companies={paginatedCompanies}
            filteredCompanies={paginatedCompanies}
            isLoading={isLoading}
            onDeleteCompanies={handleDeleteCompanies}
            onCompaniesChange={onCompaniesChange}
            favorites={favorites}
          />
        )}
      </div>
      
      {totalPages > 1 && (
        <CandidatesPagination 
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};
